import { toStdBase64 } from './base64.js';

// The wire `client_id` (S3): the gateway derives a principal's id from the X-Public-Key
// header value as `sha256(utf8(publicKeyBase64))[..16]`, little-endian u128, and emits it on
// read batches as standard padded base64 of those 16 bytes
// (`celeriant_localfirst_crypto::generate_short_client_identity` + `wire::id_to_wire`).
//
// The SDK must compute the SAME string to recognise its own writes in a read (seed/dedup).
// A locally-generated random id can NEVER match it, which silently no-ops the seed ritual and
// drops writes after an IndexedDB wipe. Derive it from the auth keypair instead, so the id is
// stable across wipes (it follows the keypair, not local storage).
//
// Cross-language proof: `deriveWireClientId('a-base64-public-key-string')` must equal the
// gateway's `wire.rs` test vector. See identity.test.ts.
export async function deriveWireClientId(publicKeyBase64: string): Promise<string> {
  return deriveShortWireId(publicKeyBase64);
}

// The gateway's `generate_short_client_identity`: sha256(utf8(input))[..16], little-endian
// u128, std padded base64. Both client_id (over the public key) and user_id (over the JWT
// `sub`) are this same transform — see celeriant_localfirst_crypto::lib.rs.
async function deriveShortWireId(input: string): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input)));
  let bin = '';
  for (let i = 0; i < 16; i++) bin += String.fromCharCode(digest[i]!);
  return btoa(bin);
}

// The wire `user_id` (D1): the gateway derives it from the verified JWT subject as
// `sha256(utf8(claims.sub))[..16]` — the SAME transform as client_id, over `sub` instead of
// the public key (`auth.rs`: `user_id = generate_short_client_identity(claims.sub.as_bytes())`).
// The SDK derives it the same way so a reducer's event envelope can attribute this client's
// OWN events to its real user id — matching the value the gateway stamps when a PEER reads
// those same events. This is matching the documented derivation, not inventing identity
// (invariant 34, exactly as deriveWireClientId does for the keypair).
export async function deriveWireUserId(sub: string): Promise<string> {
  return deriveShortWireId(sub);
}

// Reads the `sub` claim out of a JWT WITHOUT verifying its signature. The gateway is the
// verifier (it 401s a bad token on the actual request); this only learns our own subject to
// derive our own user_id for local attribution display. A forged token would at worst make a
// client mis-attribute its OWN events to itself — peers still see the gateway-verified id.
// Returns undefined for a malformed token or a missing/empty sub (→ clientId-only attribution).
export function decodeJwtSub(token: string): string | undefined {
  const parts = token.split('.');
  if (parts.length < 2) return undefined;
  try {
    const std = toStdBase64(parts[1]!);
    const bytes = Uint8Array.from(atob(std), (c) => c.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);
    const sub = (JSON.parse(json) as { sub?: unknown }).sub;
    return typeof sub === 'string' && sub.trim() !== '' ? sub : undefined;
  } catch {
    return undefined;
  }
}
